import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SnackbarProvider } from "./shared/components/Snackbar";
import { Layout } from "./routes/Layout";
import { AuthExpiredModal } from "./shared/components/AuthExpiredModal";
import { CostCapModal } from "./shared/components/CostCapModal";
import { NewClipModal } from "./shared/components/NewClipModal";
import { useTranslation } from "./shared/i18n/I18nProvider";

/**
 * Route-level code-splitting (2026-05-25, P1.3 prod-readiness gate).
 *
 * Before: every route module statically imported → single 787 KB raw
 * bundle (~238 KB gz). On a mid-tier Android over Vietnamese 3G this
 * is ~5 s of parse before first paint of a route the user may not
 * even visit.
 *
 * After: each route lazy()-loaded → main bundle = shell (Layout,
 * SnackbarProvider, AuthExpiredModal, QueryClient) + a small loader.
 * Each route ships as its own chunk that downloads on first navigation
 * to that URL. TanStack Query, react-router-dom, and the brand UI
 * primitives still ship in the shared chunk (they're imported by
 * Layout) so route transitions stay snappy after the first hit.
 *
 * Suspense fallback: minimal loader to avoid layout flash. We DON'T
 * use a Skeleton-of-the-route because per-route skeletons require
 * eagerly knowing each route's hero shape — extra coupling for
 * minimal UX win on a <300 ms chunk download over 4G.
 *
 * SignIn + OAuthCallback are NOT lazy (they're the cold-start landing
 * surface; we want them in the shell so first-time visitors don't
 * wait for an extra round-trip before they can sign in).
 */
import { SignIn } from "./routes/SignIn";
import { OAuthCallback } from "./routes/OAuthCallback";

const Dashboard = lazy(() => import("./routes/Dashboard").then((m) => ({ default: m.Dashboard })));
const Products = lazy(() => import("./routes/Products").then((m) => ({ default: m.Products })));
const ProductDetail = lazy(() => import("./routes/ProductDetail").then((m) => ({ default: m.ProductDetail })));
const ProductFacts = lazy(() => import("./routes/ProductFacts").then((m) => ({ default: m.ProductFacts })));
const ClipTemplatesLibrary = lazy(() => import("./routes/ClipTemplatesLibrary").then((m) => ({ default: m.ClipTemplatesLibrary })));
const ClipTemplateDetail = lazy(() => import("./routes/ClipTemplateDetail").then((m) => ({ default: m.ClipTemplateDetail })));
const ClipNew = lazy(() => import("./routes/ClipNew").then((m) => ({ default: m.ClipNew })));
const Projects = lazy(() => import("./routes/Projects").then((m) => ({ default: m.Projects })));
const ProjectDetail = lazy(() => import("./routes/ProjectDetail").then((m) => ({ default: m.ProjectDetail })));
const EditorLite = lazy(() => import("./routes/EditorLite").then((m) => ({ default: m.EditorLite })));
const Render = lazy(() => import("./routes/Render").then((m) => ({ default: m.Render })));
const Export = lazy(() => import("./routes/Export").then((m) => ({ default: m.Export })));
const Billing = lazy(() => import("./routes/Billing").then((m) => ({ default: m.Billing })));
const AiActivity = lazy(() => import("./routes/AiActivity").then((m) => ({ default: m.AiActivity })));
const Settings = lazy(() => import("./routes/Settings").then((m) => ({ default: m.Settings })));
const SettingsTheme = lazy(() => import("./routes/SettingsTheme").then((m) => ({ default: m.SettingsTheme })));
const SettingsLanguage = lazy(() => import("./routes/SettingsLanguage").then((m) => ({ default: m.SettingsLanguage })));
const SettingsNotifications = lazy(() => import("./routes/SettingsNotifications").then((m) => ({ default: m.SettingsNotifications })));
const SettingsOnboarding = lazy(() => import("./routes/SettingsOnboarding").then((m) => ({ default: m.SettingsOnboarding })));
const ProfileEdit = lazy(() => import("./routes/ProfileEdit").then((m) => ({ default: m.ProfileEdit })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

/** Minimal centered spinner shown while a route chunk loads. Mirrors
 *  the brand indigo so it doesn't feel jarring against any route's
 *  shell color. ~16 ms render budget keeps it light. */
function RouteLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-subtle border-t-primary-hover" />
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route element={<Layout titleKey="routes.dashboard" />}>
              <Route path="/dashboard" element={<Dashboard />} />
            </Route>
            <Route element={<Layout titleKey="routes.products" />}>
              <Route path="/products" element={<Products />} />
            </Route>
            <Route element={<Layout titleKey="routes.productDetail" />}>
              <Route path="/products/:id" element={<ProductDetail />} />
            </Route>
            <Route element={<Layout titleKey="routes.productFacts" />}>
              <Route path="/products/:id/facts" element={<ProductFacts />} />
            </Route>
            <Route element={<Layout titleKey="routes.clipTemplates" />}>
              <Route path="/clip-templates" element={<ClipTemplatesLibrary />} />
            </Route>
            <Route element={<Layout titleKey="routes.clipTemplateDetail" />}>
              <Route path="/clip-templates/:type" element={<ClipTemplateDetail />} />
            </Route>
            <Route element={<Layout titleKey="routes.clipNew" />}>
              <Route path="/clip/new" element={<ClipNew />} />
            </Route>
            <Route element={<Layout titleKey="routes.clips" />}>
              <Route path="/projects" element={<Projects />} />
            </Route>
            <Route element={<Layout titleKey="routes.projectScript" />}>
              <Route path="/projects/:id" element={<ProjectDetail />} />
            </Route>
            <Route element={<Layout titleKey="routes.editor" />}>
              <Route path="/projects/:id/editor" element={<EditorLite />} />
            </Route>
            <Route element={<Layout titleKey="routes.render" />}>
              <Route path="/projects/:id/render" element={<Render />} />
            </Route>
            <Route element={<Layout titleKey="routes.export" />}>
              <Route path="/projects/:id/export" element={<Export />} />
            </Route>
            <Route element={<Layout titleKey="routes.billing" />}>
              <Route path="/settings/billing" element={<Billing />} />
            </Route>
            <Route element={<Layout titleKey="routes.aiActivity" />}>
              <Route path="/settings/ai-activity" element={<AiActivity />} />
            </Route>
            <Route element={<Layout titleKey="routes.settings" />}>
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route element={<Layout titleKey="routes.settingsTheme" />}>
              <Route path="/settings/theme" element={<SettingsTheme />} />
            </Route>
            <Route element={<Layout titleKey="routes.settingsLanguage" />}>
              <Route path="/settings/language" element={<SettingsLanguage />} />
            </Route>
            <Route element={<Layout titleKey="routes.settingsNotifications" />}>
              <Route
                path="/settings/notifications"
                element={<SettingsNotifications />}
              />
            </Route>
            <Route element={<Layout titleKey="routes.profileEdit" />}>
              {/* Canonical URL per W-P-01 spec (browser chrome shows
                  /settings/profile). Keep /settings/profile-edit alias
                  for any deep-links saved before D4. */}
              <Route path="/settings/profile" element={<ProfileEdit />} />
              <Route path="/settings/profile-edit" element={<ProfileEdit />} />
            </Route>
            {/* Onboarding tour replay — added 2026-05-22 PM (Vincent Settings
                merge). Web counterpart of mobile /(tabs)/profile/onboarding.
                Reuses profileOnboarding.* i18n keys (vi/en/zh ready). */}
            <Route element={<Layout titleKey="routes.settingsOnboarding" />}>
              <Route path="/settings/onboarding" element={<SettingsOnboarding />} />
            </Route>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="*"
              element={<NotFound />}
            />
          </Routes>
        </Suspense>
        {/* Global auth-expired modal — mounted OUTSIDE <Routes> so it
            survives Layout's `<Navigate to="/sign-in" />` when session
            goes null. Listens to `isale:auth-expired` event from the
            apiRequest layer (shared/api/client.ts). Shows a friendly
            countdown + redirect UX instead of the user seeing a raw
            "Missing or malformed Authorization header" toast. */}
        <AuthExpiredModal />
        {/* F-Q1: global cost-cap upgrade prompt — fires on `isale:cost-cap-reached`
            from the apiRequest layer (402 QUOTA_COST_CAP_REACHED). */}
        <CostCapModal />
        {/* Global create-clip modal — opened from anywhere via the
            `isale:new-clip` event (cradle FAB on non-Products tabs + Dashboard/
            Projects CTAs). Consolidates the old per-screen create-clip entries
            into one, mirroring the mobile-native center FAB. */}
        <NewClipModal />
      </BrowserRouter>
      </SnackbarProvider>
    </QueryClientProvider>
  );
}

/**
 * 404 fallback. Extracted to a component (GA-W-07) so the back-link copy
 * runs through i18n — it was the last hardcoded VN string on web routes.
 */
function NotFound() {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen items-center justify-center bg-canvas">
      <div className="text-center">
        <div className="text-6xl font-extrabold text-primary-hover">404</div>
        <a
          href="/dashboard"
          className="mt-4 inline-block text-sm font-bold text-primary-strong hover:underline"
        >
          {t("common.backToDashboard")}
        </a>
      </div>
    </div>
  );
}
